/**
 * Hybrid logical clock (PLAN §6.1, D6).
 *
 * Every writer — an SPA session, a node, a Lambda — keeps one of these, and
 * every row patch carries the timestamp it issued. The per-field LWW merge
 * (`lww.ts`) decides a winner by comparing them, so the two properties that
 * matter are:
 *
 *   1. **Monotonic per writer.** `now()` never returns a value less than or
 *      equal to anything this clock has issued or received, even if the wall
 *      clock steps backwards (NTP correction, VM resume).
 *   2. **Unique across writers.** The node id is part of the timestamp, so two
 *      writers can never issue the same value — which is the precondition that
 *      makes the merge commutative (see `lww.ts`).
 *
 * **Wire format: `<ms>-<counter>-<nodeId>`**, with `ms` zero-padded to 13
 * digits and `counter` to 5. The padding is what makes plain string order equal
 * the clock order — a DynamoDB sort key or a SQLite `ORDER BY` on the raw value
 * is correct without parsing. `compareHlc` compares the parsed tuple anyway,
 * and `hlc.test.ts` holds the two orders against each other.
 *
 * The wall clock is a parameter (`wallClockMs`), never `Date.now()` reached
 * for: this package is pure, and the tests drive a clock that goes backwards.
 */

/** A parsed HLC timestamp. */
export type HlcTimestamp = {
  /** Logical milliseconds: max(wall clock, anything seen). */
  readonly ms: number;
  /** Tie-breaker within one millisecond. */
  readonly counter: number;
  /** The issuing writer. Part of the value, so no two writers collide. */
  readonly nodeId: string;
};

/** A writer's clock. Holds the last timestamp it issued or received. */
export type HlcClock = {
  /** A fresh timestamp for a local event (a patch this writer makes). */
  readonly now: () => string;
  /**
   * Fold a remote timestamp in (a patch this writer applies), returning the
   * timestamp of the receive event. Afterwards `now()` is greater than
   * `remote`. Run `clampFutureHlc` BEFORE this on a server: receiving a
   * far-future value would drag this clock forward with it.
   */
  readonly receive: (remote: string) => string;
};

/** 13 digits of milliseconds covers every date up to the year 2286. */
const MS_DIGITS = 13;
/** 5 digits of counter: 100 000 events inside one millisecond before rolling. */
const COUNTER_DIGITS = 5;
export const MAX_COUNTER = 10 ** COUNTER_DIGITS - 1;
const MAX_MS = 10 ** MS_DIGITS - 1;

/**
 * Node ids are identifiers, not free text: no whitespace (the value is embedded
 * in keys and logs) and bounded length (it is repeated in every field's `h`).
 * Hyphens are allowed so a UUID works; the parser anchors on the two fixed-width
 * numeric fields, so a hyphen in the node id is unambiguous.
 */
const NODE_ID = /^[A-Za-z0-9._:-]{1,64}$/;
const HLC = /^(\d{13})-(\d{5})-([A-Za-z0-9._:-]{1,64})$/;

/**
 * How far ahead of the server's own clock a client timestamp may be before the
 * server re-stamps it (PLAN §6.1: "more than 5 s ahead"). Without the clamp a
 * client whose clock is an hour fast wins every conflict for an hour.
 */
export const MAX_FUTURE_SKEW_MS = 5_000;

/** Whether `nodeId` is usable as the writer part of an HLC. */
export function isValidNodeId(nodeId: string): boolean {
  return NODE_ID.test(nodeId);
}

function assertNodeId(nodeId: string): void {
  if (!isValidNodeId(nodeId)) {
    throw new Error(`Invalid HLC node id ${JSON.stringify(nodeId)}: expected 1-64 of [A-Za-z0-9._:-]`);
  }
}

/** Parse an HLC string, or null when it is not one. */
export function tryParseHlc(value: string): HlcTimestamp | null {
  const match = HLC.exec(value);
  if (match === null) return null;
  const [, ms, counter, nodeId] = match;
  if (ms === undefined || counter === undefined || nodeId === undefined) return null;
  return { ms: Number(ms), counter: Number(counter), nodeId };
}

/** Whether `value` is a well-formed HLC string. */
export function isValidHlc(value: string): boolean {
  return tryParseHlc(value) !== null;
}

/** Parse an HLC string, throwing on anything malformed. */
export function parseHlc(value: string): HlcTimestamp {
  const parsed = tryParseHlc(value);
  if (parsed === null) {
    throw new Error(`Invalid HLC ${JSON.stringify(value)}: expected "<13-digit ms>-<5-digit counter>-<nodeId>"`);
  }
  return parsed;
}

/** Format a timestamp in the wire form. Throws on out-of-range parts. */
export function formatHlc(ts: HlcTimestamp): string {
  if (!Number.isInteger(ts.ms) || ts.ms < 0 || ts.ms > MAX_MS) {
    throw new Error(`HLC ms out of range: ${String(ts.ms)}`);
  }
  if (!Number.isInteger(ts.counter) || ts.counter < 0 || ts.counter > MAX_COUNTER) {
    throw new Error(`HLC counter out of range: ${String(ts.counter)}`);
  }
  assertNodeId(ts.nodeId);
  return `${String(ts.ms).padStart(MS_DIGITS, '0')}-${String(ts.counter).padStart(COUNTER_DIGITS, '0')}-${ts.nodeId}`;
}

function compareTimestamps(x: HlcTimestamp, y: HlcTimestamp): number {
  if (x.ms !== y.ms) return x.ms < y.ms ? -1 : 1;
  if (x.counter !== y.counter) return x.counter < y.counter ? -1 : 1;
  if (x.nodeId === y.nodeId) return 0;
  return x.nodeId < y.nodeId ? -1 : 1;
}

/** Total order over HLC strings: negative, zero or positive. Throws on malformed input. */
export function compareHlc(a: string, b: string): number {
  return compareTimestamps(parseHlc(a), parseHlc(b));
}

/**
 * The successor of `(ms, counter)` for this node: the next counter, or — when
 * the counter is exhausted — the next logical millisecond.
 *
 * Rolling into the next millisecond rather than throwing is standard HLC
 * behaviour: the logical `ms` is allowed to run ahead of the wall clock, and it
 * keeps `now()` total. It only happens after 100 000 events inside one
 * millisecond, which no writer here produces.
 */
function tick(ms: number, counter: number, nodeId: string): HlcTimestamp {
  if (counter < MAX_COUNTER) return { ms, counter: counter + 1, nodeId };
  return { ms: ms + 1, counter: 0, nodeId };
}

/**
 * A new clock for `nodeId`, reading physical time from `wallClockMs`.
 *
 * `initial` seeds the clock with the highest timestamp this writer has already
 * issued (restored from storage after a restart), or null for a fresh writer.
 * Passing it is what keeps a restarted node from re-issuing a value it issued
 * before a backwards wall-clock step.
 */
export function createHlcClock(nodeId: string, wallClockMs: () => number, initial: string | null): HlcClock {
  assertNodeId(nodeId);
  let last: HlcTimestamp = initial === null ? { ms: 0, counter: 0, nodeId } : { ...parseHlc(initial), nodeId };

  const now = (): string => {
    const physical = Math.floor(wallClockMs());
    last = physical > last.ms ? { ms: physical, counter: 0, nodeId } : tick(last.ms, last.counter, nodeId);
    return formatHlc(last);
  };

  const receive = (remote: string): string => {
    const r = parseHlc(remote);
    const physical = Math.floor(wallClockMs());
    const ms = Math.max(last.ms, r.ms, physical);
    if (ms === physical && ms !== last.ms && ms !== r.ms) {
      last = { ms, counter: 0, nodeId };
    } else {
      // The largest counter already used at `ms`, from either side, then tick.
      const localAtMs = last.ms === ms ? last.counter : -1;
      const remoteAtMs = r.ms === ms ? r.counter : -1;
      last = tick(ms, Math.max(localAtMs, remoteAtMs), nodeId);
    }
    return formatHlc(last);
  };

  return { now, receive };
}

/**
 * Whether `hlc` is more than `maxAheadMs` ahead of `nowMs`.
 */
export function isTooFarInFuture(hlc: string, nowMs: number, maxAheadMs: number): boolean {
  return parseHlc(hlc).ms > nowMs + maxAheadMs;
}

/**
 * The future clamp (PLAN §6.1): a client timestamp more than `maxAheadMs`
 * ahead of the server's wall clock is replaced by a fresh server timestamp;
 * anything else passes through unchanged.
 *
 * `restamp` is called only when clamping — usually the server clock's `now` —
 * so an in-range patch does not consume a tick. The server should then
 * `receive()` the RESULT, never the raw client value, or its own clock is
 * dragged into the future along with the patch.
 */
export function clampFutureHlc(hlc: string, nowMs: number, maxAheadMs: number, restamp: () => string): string {
  return isTooFarInFuture(hlc, nowMs, maxAheadMs) ? restamp() : hlc;
}
